import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Gift, UserCog, Zap, ArrowRight, RefreshCw, Users2, BellOff, Mail, Send, Skull, AlertTriangle, Flame, Trash2, Ban, RotateCcw, Filter, Search, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function SettingsPage() {
  const [bonus, setBonus] = useState("");
  const [current, setCurrent] = useState(null);
  const [autoAssign, setAutoAssign] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [digestEmail, setDigestEmail] = useState("");
  const [digestSavedEmail, setDigestSavedEmail] = useState("");
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestSending, setDigestSending] = useState(false);

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await api.get("/admin/settings/round-robin-preview");
      setPreview(r.data);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setPreviewLoading(false); }
  };

  const load = async () => {
    try {
      const r = await api.get("/settings/referral");
      setCurrent(r.data.referral_bonus_amount);
      setBonus(String(r.data.referral_bonus_amount));
    } catch (e) { toast.error(errorMessage(e)); }
    try {
      const r = await api.get("/settings/auto-assignment");
      setAutoAssign(!!r.data.enabled);
    } catch {}
    try {
      const r = await api.get("/admin/settings/digest-email");
      setDigestEmail(r.data.email || "");
      setDigestSavedEmail(r.data.email || "");
    } catch {}
    loadPreview();
  };
  useEffect(() => { load(); }, []);

  const saveDigestEmail = async () => {
    const email = (digestEmail || "").trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Enter a valid email");
    setDigestSaving(true);
    try {
      await api.post("/admin/settings/digest-email", { email });
      setDigestSavedEmail(email);
      toast.success(email ? "Digest email saved" : "Digest email cleared");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setDigestSaving(false); }
  };

  const sendDigestNow = async () => {
    if (!digestSavedEmail) return toast.error("Save an email first");
    setDigestSending(true);
    try {
      const r = await api.post("/admin/reports/send-weekly-digest");
      toast.success(`Digest sent to ${r.data.email}`);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setDigestSending(false); }
  };

  const saveBonus = async () => {
    if (bonus === "" || Number(bonus) < 0) return toast.error("Enter a valid amount");
    try {
      await api.post("/admin/settings/referral", { referral_bonus_amount: Number(bonus) });
      toast.success("Referral bonus updated");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const toggleAuto = async (next) => {
    setAutoAssign(next);
    try {
      await api.post("/admin/settings/auto-assignment", { enabled: next });
      toast.success(`Auto-assignment ${next ? "ON" : "OFF"}`);
      loadPreview();
    } catch (e) { toast.error(errorMessage(e)); setAutoAssign(!next); }
  };

  const toggleSkip = async (staffId, skip, name, opts = {}) => {
    try {
      const body = { skip };
      if (skip) {
        if (opts.hours) body.hours = opts.hours;
        if (opts.until_iso) body.until_iso = opts.until_iso;
      }
      await api.post(`/admin/office-staff/${staffId}/skip-rr`, body);
      const detail = skip
        ? (opts.hours ? `for ${opts.hours}h`
           : opts.until_iso ? `until ${new Date(opts.until_iso).toLocaleString("en-IN")}`
           : "until end of day (IST)")
        : "";
      toast.success(skip ? `${name} muted ${detail}` : `${name} back in round-robin`);
      loadPreview();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const pickCustomSkip = (staffId, name) => {
    const raw = window.prompt("Skip until (YYYY-MM-DD HH:MM, local time):", "");
    if (!raw) return;
    const parsed = new Date(raw.replace(" ", "T"));
    if (isNaN(parsed.getTime()) || parsed <= new Date()) {
      toast.error("Enter a valid future date/time");
      return;
    }
    toggleSkip(staffId, true, name, { until_iso: parsed.toISOString() });
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      {/* Auto-Assignment */}
      <div className="card-surface p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-amber-400 mt-1"/>
            <div>
              <div className="font-heading text-white text-lg">Auto-Assign Website Leads</div>
              <div className="text-sm text-slate-500 mt-1">
                When ON, every new lead from the website / landing page is instantly assigned to an active Telecaller in round-robin order.
                When OFF, new leads land in "pending_verification" and admin must assign manually.
              </div>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0" data-testid="auto-assign-toggle">
            <input type="checkbox" checked={autoAssign} onChange={e => toggleAuto(e.target.checked)} className="sr-only peer"/>
            <div className="w-14 h-8 bg-slate-800 rounded-full peer-checked:bg-amber-500 transition-colors relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-transform peer-checked:after:translate-x-6"/>
          </label>
        </div>
        <div className={`mt-4 text-xs font-bold uppercase tracking-widest ${autoAssign ? "text-emerald-400" : "text-slate-500"}`}>
          Status: {autoAssign ? "ON — Round-Robin Active" : "OFF — Manual Assignment"}
        </div>

        {/* Round-Robin Preview */}
        <div className="mt-5 border-t border-white/5 pt-5" data-testid="rr-preview">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users2 className="w-4 h-4 text-slate-400"/>
              <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Round-Robin Preview</div>
            </div>
            <button
              type="button"
              onClick={loadPreview}
              disabled={previewLoading}
              data-testid="rr-refresh-btn"
              className="text-[11px] uppercase tracking-widest text-slate-500 hover:text-amber-400 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${previewLoading ? "animate-spin" : ""}`}/> Refresh
            </button>
          </div>

          {!preview && <div className="text-sm text-slate-500">Loading queue…</div>}

          {preview && preview.next && (
            <div className={`rounded-lg p-4 border ${autoAssign ? "bg-emerald-500/5 border-emerald-500/30" : "bg-slate-950/60 border-slate-700"}`} data-testid="rr-next-card">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-slate-900 font-bold ${autoAssign ? "bg-emerald-400" : "bg-slate-600"}`}>
                  {(preview.next.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Next Lead Goes To</div>
                  <div className="font-heading text-lg text-white truncate" data-testid="rr-next-name">
                    {preview.next.name}
                    <ArrowRight className="w-4 h-4 text-amber-400 inline mx-2"/>
                    <span className="text-sm text-slate-400 font-normal">{preview.next.phone}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Load</div>
                  <div className="font-mono-alt text-amber-400 text-lg">{preview.next.assigned_count}</div>
                </div>
              </div>
              {!autoAssign && (
                <div className="mt-3 text-[11px] text-amber-400/80 italic">
                  Auto-assign is OFF — this queue is idle. New leads land unassigned.
                </div>
              )}
            </div>
          )}

          {preview && !preview.next && (
            <div className="rounded-lg p-4 bg-red-500/10 border border-red-500/30 text-sm text-red-300" data-testid="rr-empty">
              No active telecallers found. Add or reactivate a telecaller for auto-assignment to work.
            </div>
          )}

          {preview && preview.queue?.length > 0 && (
            <>
              <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                <div className="bg-slate-950/60 border border-white/5 rounded-md py-2" data-testid="rr-stat-active">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500">Active</div>
                  <div className="font-display text-lg text-emerald-400">{preview.active_telecallers}</div>
                </div>
                <div className="bg-slate-950/60 border border-white/5 rounded-md py-2" data-testid="rr-stat-muted">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500">Muted Today</div>
                  <div className="font-display text-lg text-slate-300">{preview.muted || 0}</div>
                </div>
                <div className="bg-slate-950/60 border border-white/5 rounded-md py-2" data-testid="rr-stat-suspended">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500">Suspended</div>
                  <div className="font-display text-lg text-amber-400">{preview.suspended}</div>
                </div>
                <div className="bg-slate-950/60 border border-white/5 rounded-md py-2" data-testid="rr-stat-total">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500">Total</div>
                  <div className="font-display text-lg text-white">{preview.total_telecallers}</div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowQueue(v => !v)}
                data-testid="rr-toggle-queue"
                className="mt-3 text-[11px] uppercase tracking-widest text-slate-400 hover:text-amber-400"
              >
                {showQueue ? "Hide" : "Show"} full queue ({preview.queue.length})
              </button>

              {showQueue && (
                <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-white/5 divide-y divide-white/5" data-testid="rr-queue-list">
                  {preview.queue.map((t, i) => {
                    const isNext = !t.muted && preview.next && preview.next.id === t.id;
                    return (
                      <div key={t.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${isNext ? "bg-emerald-500/5" : ""} ${t.muted ? "opacity-60" : ""}`} data-testid={`rr-queue-row-${i}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${isNext ? "bg-emerald-400 text-slate-900" : t.muted ? "bg-slate-800 text-slate-500" : "bg-slate-800 text-slate-400"}`}>
                          {t.muted ? <BellOff className="w-3 h-3"/> : i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white truncate flex items-center gap-2">
                            {t.name}
                            {t.muted && <span className="text-[9px] uppercase tracking-widest text-slate-500 border border-slate-700 rounded px-1.5 py-0.5">Muted today</span>}
                          </div>
                          <div className="text-[10px] text-slate-500 font-mono-alt">{t.phone}</div>
                        </div>
                        <div className="text-xs text-slate-400 shrink-0">
                          <span className="font-mono-alt text-amber-400">{t.assigned_count}</span> leads
                        </div>
                        {t.muted ? (
                          <button
                            type="button"
                            onClick={() => toggleSkip(t.id, false, t.name)}
                            data-testid={`rr-skip-btn-${t.id}`}
                            className="text-[10px] uppercase tracking-widest rounded-full px-3 py-1 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 shrink-0"
                          >Un-mute</button>
                        ) : (
                          <select
                            data-testid={`rr-skip-btn-${t.id}`}
                            value=""
                            onChange={(e) => {
                              const v = e.target.value;
                              if (!v) return;
                              if (v === "eod") toggleSkip(t.id, true, t.name);
                              else if (v === "custom") pickCustomSkip(t.id, t.name);
                              else toggleSkip(t.id, true, t.name, { hours: parseInt(v, 10) });
                              e.target.value = "";
                            }}
                            className="bg-slate-950 border border-slate-700 text-slate-400 hover:text-amber-400 rounded-full text-[10px] uppercase tracking-widest px-2 py-1 shrink-0 cursor-pointer"
                          >
                            <option value="">Skip…</option>
                            <option value="eod">End of Day (IST)</option>
                            <option value="1">1 hour</option>
                            <option value="3">3 hours</option>
                            <option value="24">24 hours</option>
                            <option value="custom">Pick date/time…</option>
                          </select>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Weekly Digest Email */}
      <div className="card-surface p-6" data-testid="digest-card">
        <div className="mb-4 flex items-start gap-3">
          <Mail className="w-5 h-5 text-amber-400 mt-1"/>
          <div>
            <div className="font-heading text-lg text-white">Weekly Digest Email</div>
            <div className="text-sm text-slate-500">Sunday 8:00 PM IST — total leads, top telecaller, biggest deltas.</div>
          </div>
        </div>
        <Label className="text-slate-400 text-xs uppercase tracking-widest">Recipient email</Label>
        <div className="flex gap-2 mt-2 flex-wrap">
          <Input
            data-testid="digest-email-input"
            type="email"
            placeholder="owner@shakthi.com"
            value={digestEmail}
            onChange={e => setDigestEmail(e.target.value)}
            className="bg-slate-950 border-slate-700 max-w-sm"
          />
          <Button
            data-testid="digest-save-btn"
            onClick={saveDigestEmail}
            disabled={digestSaving || digestEmail === digestSavedEmail}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full disabled:opacity-40"
          >{digestSaving ? "Saving…" : "Save"}</Button>
          <Button
            data-testid="digest-send-now-btn"
            onClick={sendDigestNow}
            disabled={digestSending || !digestSavedEmail}
            variant="ghost"
            className="text-emerald-400 hover:bg-emerald-500/10 border border-emerald-500/30 rounded-full"
          >
            <Send className="w-4 h-4 mr-2"/> {digestSending ? "Sending…" : "Send Now"}
          </Button>
        </div>
        <div className="mt-3 text-[11px] text-slate-500">
          {digestSavedEmail
            ? <>Auto-send every Sunday 8:00 PM IST to <span className="text-amber-400 font-mono-alt">{digestSavedEmail}</span>. Clear the field and save to disable.</>
            : <>Add an email to enable the Sunday auto-send.</>}
        </div>
      </div>

      {/* Referral Bonus */}
      <div className="card-surface p-6">
        <div className="mb-4 flex items-start gap-3">
          <Gift className="w-5 h-5 text-amber-400 mt-1"/>
          <div>
            <div className="font-heading text-lg text-white">Referral Settings</div>
            <div className="text-sm text-slate-500">Applied to new referrals only. Existing leads keep their snapshotted bonus.</div>
          </div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
          <div className="text-xs uppercase tracking-widest text-amber-500 font-bold">Current bonus</div>
          <div className="font-display text-2xl text-white">₹{Number(current || 0).toLocaleString("en-IN")}</div>
        </div>
        <div>
          <Label className="text-slate-400 text-xs uppercase tracking-widest">Referral Bonus Amount (₹)</Label>
          <div className="relative mt-2 max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
            <Input data-testid="settings-bonus-input" type="number" value={bonus} onChange={e => setBonus(e.target.value.replace(/[^\d.]/g,''))} className="bg-slate-950 border-slate-700 pl-8"/>
          </div>
          <Button data-testid="settings-save-btn" onClick={saveBonus} className="mt-4 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">Save Bonus</Button>
        </div>
      </div>

      <RejectedCasesCard/>

      <DangerZone/>
    </div>
  );
}

// -------------------------------------------------------------------------
// Rejected Cases — Admin audit trail with filters + Restore capability
// -------------------------------------------------------------------------
function RejectedCasesCard() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_reason: {} });
  const [loading, setLoading] = useState(false);
  const [filterStaff, setFilterStaff] = useState("");
  const [filterReason, setFilterReason] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [includeRestored, setIncludeRestored] = useState(false);
  const [search, setSearch] = useState("");
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { include_restored: includeRestored };
      if (filterStaff) params.staff_id = filterStaff;
      if (filterReason) params.reason_category = filterReason;
      if (filterFrom) params.from_date = filterFrom;
      if (filterTo) params.to_date = filterTo;
      const r = await api.get("/admin/rejected-leads", { params });
      setRows(r.data.rows || []);
      setStaff(r.data.staff || []);
      setReasons(r.data.reasons || []);
      setStats(r.data.stats || { total: 0, by_reason: {} });
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStaff, filterReason, filterFrom, filterTo, includeRestored]);

  const restore = async () => {
    if (!restoreConfirm) return;
    setRestoring(true);
    try {
      const r = await api.post(`/admin/rejected-leads/${restoreConfirm.id}/restore`);
      toast.success(`Restored ${r.data.lead_id} back to IN pool · ${r.data.restored_at_ist}`);
      setRestoreConfirm(null);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setRestoring(false); }
  };

  const clearFilters = () => {
    setFilterStaff(""); setFilterReason(""); setFilterFrom(""); setFilterTo(""); setSearch(""); setIncludeRestored(false);
  };

  const s = (search || "").trim().toLowerCase();
  const visible = !s ? rows : rows.filter(r =>
    [r.customer_name, r.customer_phone, r.lead_id, r.rejected_by_staff_name]
      .some(v => (v || "").toLowerCase().includes(s))
  );

  return (
    <div className="card-surface p-6" data-testid="rejected-cases-card">
      <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Ban className="w-5 h-5 text-red-400 mt-1"/>
          <div>
            <div className="font-heading text-lg text-white">Rejected Cases</div>
            <div className="text-sm text-slate-500">Every lead a telecaller rejected — with full audit trail. Restore any case back to IN pool.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs" data-testid="rejected-total-badge">
            {stats.total} total
          </Badge>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="rejected-refresh-btn"
            className="text-[11px] uppercase tracking-widest text-slate-500 hover:text-amber-400 flex items-center gap-1 disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}/> Refresh
          </button>
        </div>
      </div>

      {/* Stats by reason */}
      {stats.total > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {Object.entries(stats.by_reason).map(([k, v]) => (
            <Badge key={k} className="bg-slate-800 text-slate-300 border-slate-700 text-[10px] uppercase tracking-widest">
              {k} · <span className="text-red-300 ml-1">{v}</span>
            </Badge>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-lg bg-slate-950/50 border border-slate-800 p-3 mb-4 space-y-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
          <Filter className="w-3 h-3"/> Filters
          <button type="button" onClick={clearFilters} className="ml-auto normal-case tracking-normal text-slate-500 hover:text-amber-400" data-testid="rejected-clear-filters">Clear all</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div>
            <Label className="text-[9px] uppercase tracking-widest text-slate-500">Telecaller</Label>
            <select
              data-testid="rejected-filter-staff"
              value={filterStaff}
              onChange={e => setFilterStaff(e.target.value)}
              className="mt-1 w-full bg-slate-950 border border-slate-700 text-white rounded-md px-2 py-1.5 text-xs focus:border-amber-500 focus:outline-none"
            >
              <option value="">All staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.phone})</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[9px] uppercase tracking-widest text-slate-500">Reason Category</Label>
            <select
              data-testid="rejected-filter-reason"
              value={filterReason}
              onChange={e => setFilterReason(e.target.value)}
              className="mt-1 w-full bg-slate-950 border border-slate-700 text-white rounded-md px-2 py-1.5 text-xs focus:border-amber-500 focus:outline-none"
            >
              <option value="">All reasons</option>
              {reasons.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[9px] uppercase tracking-widest text-slate-500">From</Label>
            <Input
              data-testid="rejected-filter-from"
              type="date"
              value={filterFrom}
              onChange={e => setFilterFrom(e.target.value)}
              className="bg-slate-950 border-slate-700 mt-1 h-8 text-xs"
            />
          </div>
          <div>
            <Label className="text-[9px] uppercase tracking-widest text-slate-500">To</Label>
            <Input
              data-testid="rejected-filter-to"
              type="date"
              value={filterTo}
              onChange={e => setFilterTo(e.target.value)}
              className="bg-slate-950 border-slate-700 mt-1 h-8 text-xs"
            />
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3 h-3 text-slate-500 absolute left-2 top-2.5"/>
            <Input
              data-testid="rejected-search"
              placeholder="Search customer, phone, lead ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-7 h-8 text-xs bg-slate-950 border-slate-700"
            />
          </div>
          <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer">
            <input
              type="checkbox"
              checked={includeRestored}
              onChange={e => setIncludeRestored(e.target.checked)}
              className="accent-emerald-500"
              data-testid="rejected-include-restored"
            />
            Include restored
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-800 overflow-x-auto" data-testid="rejected-cases-table">
        <table className="w-full text-xs min-w-[900px]">
          <thead>
            <tr className="bg-slate-950/80 text-slate-400 uppercase tracking-widest text-[10px]">
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Phone</th>
              <th className="text-left px-3 py-2">Reason Category</th>
              <th className="text-left px-3 py-2">Detailed Reason</th>
              <th className="text-left px-3 py-2">Rejected By</th>
              <th className="text-left px-3 py-2">Date &amp; Time (IST)</th>
              <th className="text-right px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center text-slate-500 py-6" data-testid="rejected-loading">Loading…</td></tr>
            )}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-500 py-8" data-testid="rejected-empty">
                No rejected cases match the current filters.
              </td></tr>
            )}
            {!loading && visible.map(r => (
              <tr key={r.id} className={`border-t border-slate-800 ${r.restored ? "opacity-60 bg-slate-950/40" : "hover:bg-white/5"}`} data-testid={`rejected-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="text-white font-semibold">{r.customer_name || "—"}</div>
                  <div className="text-[10px] font-mono-alt text-amber-400/70">{r.lead_id}</div>
                </td>
                <td className="px-3 py-2 font-mono-alt text-slate-300">{r.customer_phone || "—"}</td>
                <td className="px-3 py-2">
                  <Badge className="bg-red-500/15 text-red-300 border-red-500/30 text-[10px]">{r.reason_category}</Badge>
                </td>
                <td className="px-3 py-2 text-slate-300 max-w-[280px]">
                  <div className="truncate" title={r.detailed_reason}>{r.detailed_reason}</div>
                  <button type="button" onClick={() => setDetailRow(r)} className="text-[10px] text-amber-400 hover:underline flex items-center gap-1 mt-0.5" data-testid={`rejected-view-${r.id}`}>
                    <Eye className="w-3 h-3"/> View full
                  </button>
                </td>
                <td className="px-3 py-2">
                  <div className="text-white">{r.rejected_by_staff_name || "—"}</div>
                  <div className="text-[10px] font-mono-alt text-slate-500">{r.rejected_by_staff_phone || "—"}</div>
                </td>
                <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{r.rejected_at_ist}</td>
                <td className="px-3 py-2 text-right">
                  {r.restored ? (
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]" data-testid={`restored-badge-${r.id}`}>
                      Restored
                    </Badge>
                  ) : (
                    <Button
                      data-testid={`restore-btn-${r.id}`}
                      onClick={() => setRestoreConfirm(r)}
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full h-7 px-3 text-[10px]"
                    >
                      <RotateCcw className="w-3 h-3 mr-1"/> Restore
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail modal */}
      <Dialog open={!!detailRow} onOpenChange={(o) => !o && setDetailRow(null)}>
        <DialogContent className="bg-slate-900 border-red-500/30 text-white max-w-lg" data-testid="rejected-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-300 flex items-center gap-2">
              <Ban className="w-4 h-4"/> Rejection Details
            </DialogTitle>
          </DialogHeader>
          {detailRow && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Customer</div><div className="text-white">{detailRow.customer_name}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Phone</div><div className="font-mono-alt text-slate-300">{detailRow.customer_phone}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Lead ID</div><div className="font-mono-alt text-amber-400">{detailRow.lead_id}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Loan</div><div className="text-slate-300">{detailRow.loan_type || "—"} · ₹{parseFloat(detailRow.amount || 0).toLocaleString("en-IN")}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">District</div><div className="text-slate-300">{detailRow.customer_district || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Rejected At (IST)</div><div className="text-slate-300">{detailRow.rejected_at_ist}</div></div>
                <div className="col-span-2"><div className="text-[10px] uppercase tracking-widest text-slate-500">Rejected By</div><div className="text-white">{detailRow.rejected_by_staff_name} <span className="text-slate-500 font-mono-alt">· {detailRow.rejected_by_staff_phone}</span></div></div>
              </div>
              <div className="pt-2 border-t border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Reason Category</div>
                <Badge className="bg-red-500/15 text-red-300 border-red-500/30">{detailRow.reason_category}</Badge>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Detailed Reason</div>
                <div className="rounded-md bg-slate-950/60 border border-slate-800 p-3 text-slate-200 whitespace-pre-wrap">{detailRow.detailed_reason}</div>
              </div>
              {detailRow.restored && (
                <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-emerald-200 text-xs">
                  ✓ Restored by {detailRow.restored_by_admin_name} on {detailRow.restored_at_ist}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setDetailRow(null)} className="text-slate-300 hover:text-white"><X className="w-4 h-4 mr-2"/> Close</Button>
            {detailRow && !detailRow.restored && (
              <Button
                data-testid={`detail-restore-btn`}
                onClick={() => { const r = detailRow; setDetailRow(null); setRestoreConfirm(r); }}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full">
                <RotateCcw className="w-4 h-4 mr-2"/> Restore to IN Pool
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore confirmation */}
      <Dialog open={!!restoreConfirm} onOpenChange={(o) => !o && !restoring && setRestoreConfirm(null)}>
        <DialogContent className="bg-slate-900 border-emerald-500/40 text-white max-w-md" data-testid="restore-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-300 flex items-center gap-2">
              <RotateCcw className="w-4 h-4"/> Restore Lead to IN Pool?
            </DialogTitle>
          </DialogHeader>
          {restoreConfirm && (
            <div className="text-sm text-slate-300 space-y-2">
              <div>
                <b className="text-white">{restoreConfirm.customer_name}</b>
                <span className="text-slate-500 font-mono-alt"> · {restoreConfirm.customer_phone}</span>
              </div>
              <div className="text-xs text-slate-400">
                Was rejected on {restoreConfirm.rejected_at_ist} by {restoreConfirm.rejected_by_staff_name} · {restoreConfirm.reason_category}
              </div>
              <div className="text-xs text-slate-500 border-t border-slate-800 pt-2 mt-2">
                The lead will return to <b className="text-emerald-300">{restoreConfirm.rejected_by_staff_name}</b>'s active list. The audit trail is preserved.
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setRestoreConfirm(null)} disabled={restoring} className="text-slate-300 hover:text-white">Cancel</Button>
            <Button
              data-testid="confirm-restore-btn"
              onClick={restore}
              disabled={restoring}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full disabled:opacity-40"
            >
              <RotateCcw className="w-4 h-4 mr-2"/> {restoring ? "Restoring…" : "Yes, Restore"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- DANGER ZONE ---------------------------------------------
function DangerZone() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [fullOpen1, setFullOpen1] = useState(false);
  const [fullOpen2, setFullOpen2] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const runLeadsWipe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/danger/wipe-leads", { confirm: "WIPE LEADS" });
      const total = Object.values(r.data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`💥 Wiped ${total} lead-related records. Banks & staff kept.`);
      setLeadsOpen(false);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  const runFullWipe = async () => {
    if (confirmText.trim() !== "WIPE EVERYTHING") {
      toast.error('Type WIPE EVERYTHING exactly (in CAPS) to confirm');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/danger/wipe-everything", { confirm: "WIPE EVERYTHING" });
      const rem = r.data.remaining || {};
      toast.success(`💥 Entire Database Wiped! ${rem.banks || 0} Banks, ${(rem.users || 1) - 1} Staff, ${rem.leads || 0} Leads. Fresh Start!`);
      setFullOpen2(false); setFullOpen1(false); setConfirmText("");
      // Give the toast a beat, then log the admin out to force a clean re-login.
      setTimeout(() => { logout(); nav("/login"); }, 1500);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-surface border border-red-500/30 p-6 md:p-8 mt-6" data-testid="danger-zone">
      <div className="flex items-start gap-3 mb-4">
        <Skull className="w-6 h-6 text-red-400 mt-0.5"/>
        <div>
          <div className="font-heading text-xl text-red-300 flex items-center gap-2">
            DANGER ZONE — FULL RESET
          </div>
          <div className="text-xs text-red-200/70 mt-1 max-w-2xl">
            These actions are permanent and cannot be undone. Use only for accounting reset or fresh
            deployment. All wipes are audit-logged with your name + timestamp.
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 mt-4">
        <button
          type="button"
          data-testid="wipe-leads-btn"
          onClick={() => setLeadsOpen(true)}
          className="text-left rounded-xl border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 p-4 transition-colors">
          <div className="flex items-center gap-2 text-amber-300 font-heading">
            <Trash2 className="w-4 h-4"/> Delete Only Leads
          </div>
          <div className="text-[11px] text-amber-100/70 mt-1">
            Removes every lead, source pool row, notification, refund + boost. Banks & staff logins
            are kept — the app stays configured.
          </div>
        </button>
        <button
          type="button"
          data-testid="wipe-everything-btn"
          onClick={() => setFullOpen1(true)}
          className="text-left rounded-xl border-2 border-red-500/60 bg-red-500/10 hover:bg-red-500/20 p-4 transition-colors">
          <div className="flex items-center gap-2 text-red-300 font-heading">
            <Flame className="w-4 h-4"/> 🔥 DELETE EVERYTHING — Banks, Staff, Leads — FULL WIPE
          </div>
          <div className="text-[11px] text-red-100/70 mt-1">
            Nukes all banks, bank staff logins, office staff logins, leads, targets, audit history.
            Only your own admin login is preserved so you can rebuild from scratch.
          </div>
        </button>
      </div>

      {/* Delete-only-leads confirm */}
      <Dialog open={leadsOpen} onOpenChange={(v) => !v && setLeadsOpen(false)}>
        <DialogContent className="bg-slate-900 border-amber-500/40 text-white max-w-md" data-testid="wipe-leads-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4"/> Delete All Leads?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-300 space-y-2">
            <div>This will remove all lead-related records: leads, source pool, notifications, refunds, targets, boost schedules.</div>
            <div className="text-slate-400 text-xs">Banks and staff logins are kept intact.</div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="secondary" onClick={() => setLeadsOpen(false)} className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">Cancel</Button>
            <Button data-testid="confirm-wipe-leads" disabled={busy} onClick={runLeadsWipe}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
              {busy ? "Wiping…" : "Yes, Delete Leads"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full wipe — modal 1 */}
      <Dialog open={fullOpen1} onOpenChange={(v) => !v && setFullOpen1(false)}>
        <DialogContent className="bg-slate-900 border-red-500/60 text-white max-w-md" data-testid="wipe-everything-dialog-1">
          <DialogHeader>
            <DialogTitle className="text-red-300 flex items-center gap-2">
              <Skull className="w-4 h-4"/> ⚠️ Are You Sure?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-300 space-y-2">
            <div>This will delete <b className="text-red-300">ALL Banks</b>, <b className="text-red-300">ALL Bank Staff logins</b>, <b className="text-red-300">ALL Office Staff logins</b>, and <b className="text-red-300">ALL Leads</b>.</div>
            <div>Your entire app will become <b>EMPTY like new</b>. You'll need to recreate Banks and Staff from scratch.</div>
            <div className="text-xs text-slate-400">Your own admin login is preserved so you can log back in and rebuild.</div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="secondary" onClick={() => setFullOpen1(false)} className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">Cancel</Button>
            <Button data-testid="continue-wipe-everything" onClick={() => { setFullOpen1(false); setFullOpen2(true); setConfirmText(""); }}
              className="bg-red-500 hover:bg-red-400 text-white font-bold rounded-full">
              Yes, Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full wipe — modal 2 (type-to-confirm) */}
      <Dialog open={fullOpen2} onOpenChange={(v) => !v && (setFullOpen2(false), setConfirmText(""))}>
        <DialogContent className="bg-slate-900 border-red-500/60 text-white max-w-md" data-testid="wipe-everything-dialog-2">
          <DialogHeader>
            <DialogTitle className="text-red-300 flex items-center gap-2">
              <Flame className="w-4 h-4"/> Final Confirmation
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-300 space-y-3">
            <div>Type <code className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-200 font-mono-alt">WIPE EVERYTHING</code> exactly (in CAPS) to confirm.</div>
            <Input
              data-testid="wipe-confirm-input"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="Type WIPE EVERYTHING"
              className="bg-slate-950 border-red-500/40 font-mono-alt text-red-200"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="secondary" onClick={() => { setFullOpen2(false); setConfirmText(""); }} className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">Cancel</Button>
            <Button
              data-testid="confirm-wipe-everything"
              disabled={busy || confirmText.trim() !== "WIPE EVERYTHING"}
              onClick={runFullWipe}
              className="bg-red-500 hover:bg-red-400 text-white font-bold rounded-full disabled:opacity-40 disabled:cursor-not-allowed">
              {busy ? "Wiping…" : "I Understand, Wipe Everything"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
